import { AuditMatch, AuditSchedule, ScheduleStatusReportItem } from '@mi-front/core';

/**
 * Joins unique non-empty schedule field values. Rows are grouped by customId,
 * so advertiser/campaign/order are usually the same across schedules.
 */
export function uniqueScheduleField(
  schedules: AuditSchedule[],
  field: 'advertiser' | 'campaign' | 'order' | 'version',
): string {
  const values = schedules
    .map((schedule) => schedule[field])
    .filter((value): value is string => value != null && value !== '');

  return [...new Set(values)].join(', ');
}

/** Extra airings exist only when total matches exceed those linked to a schedule. */
export function hasPossibleUnmatchedMatches(
  status: Pick<ScheduleStatusReportItem['status'], 'matchesCount' | 'matchesWithScheduleCount'>,
): boolean {
  return status.matchesCount > status.matchesWithScheduleCount;
}

/** Matches that did not confirm against any schedule. */
export function getUnmatchedMatches(matches: readonly AuditMatch[]): AuditMatch[] {
  return matches.filter((match) => match.matchedSchedule == null);
}

/** Logger stream target for a schedule row (spot or mention match). */
export type ScheduleStreamMatch = {
  datetime: string;
  durationMs?: number;
  tab: 'audiomatches' | 'livementions';
};

/** Resolves match datetime and logger tab for a schedule, when a match exists. */
export function getScheduleStreamMatch(schedule: AuditSchedule): ScheduleStreamMatch | null {
  const spotMatch = schedule.matches?.[0];

  if (spotMatch) {
    return {
      datetime: spotMatch.datetime,
      durationMs: spotMatch.playedDuration,
      tab: 'audiomatches',
    };
  }

  if (schedule.mentionMatch) {
    return {
      datetime: schedule.mentionMatch.datetime,
      tab: 'livementions',
    };
  }

  return null;
}
